import deepEqual from "fast-deep-equal";
import { useTranslation } from "react-i18next";
import { Redirect, useParams } from "react-router-dom";

import { useAccountIds } from "../../../../auth/accessToken/components/withAccountIds";
import { LoadingOverlay } from "../../../../components/Loading";
import {
    fromMonetaryAmountString,
    toMonetaryAmountString,
} from "../../../../helpers/formatters";
import { BillingPayoutRule } from "../../../../types/billing";
import { BillingPayoutRuleDestination } from "../../../../types/orders/generated";
import { useLocations } from "../../../locations/hooks/useLocations";
import { EXAMPLE_VALUE_CURRENCY } from "../../constants";
import { usePayoutRule } from "../../hooks/usePayoutRule";
import PayoutRuleForm from "../../newPayoutRule/components/PayoutRuleForm";
import { PayoutRuleFormikValues } from "../../newPayoutRule/components/types";
import { PayoutDestinationGroup } from "../../payoutRules/types";

interface MatchParams {
    accountId: string;
    payoutRuleId: string;
}

const mapDestinations = (
    destinations: BillingPayoutRuleDestination[],
    lng: string,
): BillingPayoutRuleDestination[] => {
    return destinations.map((dest) => ({
        ...dest,
        value:
            dest.type === "flat_amount"
                ? toMonetaryAmountString(
                      lng,
                      parseFloat(dest.value || "0"),
                      EXAMPLE_VALUE_CURRENCY,
                  )
                : dest.value,
        destinations: dest.destinations
            ? mapDestinations(
                  dest.destinations as BillingPayoutRuleDestination[],
                  lng,
              )
            : undefined,
    })) as BillingPayoutRuleDestination[];
};

export const PayoutRuleEdit = () => {
    const { i18n } = useTranslation();
    const { accountId, payoutRuleId } = useParams<MatchParams>();
    const { locations } = useLocations();
    const { accountIds } = useAccountIds();
    const { payoutRule, putPayoutRuleData, putPayoutRule, puttingPayoutRule } =
        usePayoutRule(payoutRuleId);
    const lng = i18n.language;

    if (!payoutRule) {
        return <LoadingOverlay />;
    }

    const initialValues = {
        rule_id: payoutRule.rule_id,
        rule_type: payoutRule.rule_type,
        destinations: mapDestinations(payoutRule.destinations, lng),
    } as PayoutRuleFormikValues;

    const onSubmit = (values: PayoutRuleFormikValues) => {
        if (!deepEqual(values, initialValues) && accountId && payoutRuleId) {
            const mapDestinations = (
                destinations: PayoutDestinationGroup,
            ): PayoutDestinationGroup => {
                return destinations.map((dest) => {
                    // destination and destinations can't be defined at the same time
                    return {
                        ...dest,
                        value:
                            dest.type === "flat_amount"
                                ? fromMonetaryAmountString(
                                      lng,
                                      dest.value as string,
                                      EXAMPLE_VALUE_CURRENCY,
                                  ).toString()
                                : dest.value,
                        destination: dest.destination
                            ? dest.destination
                            : undefined,
                        destinations:
                            dest.destinations && dest.destinations.length > 0
                                ? mapDestinations(
                                      dest.destinations as PayoutDestinationGroup,
                                  )
                                : undefined,
                    };
                });
            };

            const newPayoutRule: BillingPayoutRule = {
                rule_id: values.rule_id,
                rule_type: values.rule_type,
                destinations: mapDestinations(
                    values.destinations,
                ) as BillingPayoutRuleDestination[],
            };
            putPayoutRule({
                accountId,
                payoutRule: newPayoutRule,
                payoutRuleId,
            });
        }
    };

    if (putPayoutRuleData) {
        return (
            <Redirect
                to={`/${accountId}/settings/payout-rules/${payoutRuleId}`}
            />
        );
    }

    return (
        <PayoutRuleForm
            initialValues={{ ...initialValues, accountId }}
            accountIds={accountIds}
            onSubmit={onSubmit}
            isLoading={puttingPayoutRule}
            isEdit
            locations={locations}
        />
    );
};
